/**
 * The hazard lane under each list group and on the detail screen (target 4a):
 * one segment per slice on a time window, a tick at each midnight in the
 * place's timezone, and the "Now" line. Geometry only, as fractions of the
 * window (0..1); each app draws it. The period rows under the lane are its
 * text twin, so the lane itself is hidden from screen readers in both apps.
 *
 * Imports `./model`, `./time` and `../Dates`, never the utils barrel
 * (`importCycles.test.ts`).
 */
import type {WeatherWarning} from '../../api/types';
import * as Dates from '../Dates';
import {groupByHazard} from './model';
import {laneDayLabel, WarningTimeOptions} from './time';
import type {
  WarningListGroup,
  WarningListSlice,
  WarningListViewModel,
  WarningTone,
} from './view';

/** How far back the window reaches when everything starts later. */
export const LANE_LEAD_SECONDS = 3 * 3600;
/** Past the last end, so a 23:59:59 end does not sit on the edge. */
const LANE_TAIL_SECONDS = 60;
/** How far an open end ("until further notice") is drawn past the last start. */
const OPEN_END_SECONDS = 24 * 3600;
/** A segment carries its severity word only when at least this wide... */
const WORD_MIN_WIDTH = 0.14;
/** ...and only when the lane has at most this many segments. */
const WORD_MAX_SEGMENTS = 4;
/** A midnight tick this close to the right edge would collide with it. */
const TICK_MAX_LEFT = 0.9;
/** Ticks drawn at most: two weeks of a lane is already unreadable. */
const MAX_TICKS = 14;

/** Paint order: the worse level last, so a milder overlapping slice never hides it. */
const TONE_RANK: Record<WarningTone, number> = {
  warningUnknown: 0,
  warningMinor: 1,
  warningModerate: 2,
  warningSevere: 3,
  warningExtreme: 4,
};

export type WarningLaneSlice = Pick<
  WarningListSlice,
  'id' | 'tone' | 'severity' | 'start' | 'end'
>;

export type WarningLaneSegment = {
  id: number;
  tone: WarningTone;
  /** The severity word to print in the segment; null for unknown. */
  severity: string | null;
  /** Fraction of the window from its start. */
  left: number;
  /** Fraction of the window it covers. Segments come mildest first (paint order). */
  width: number;
  /** Whether the segment is wide enough (and the lane sparse enough) for its word. */
  showWord: boolean;
};

export type WarningLaneTick = {
  /** Epoch seconds of the midnight. */
  at: number;
  left: number;
  /** "Today", "Tomorrow", then the weekday. */
  label: string;
};

export type WarningLane = {
  /** The window, epoch seconds. */
  start: number;
  end: number;
  segments: WarningLaneSegment[];
  ticks: WarningLaneTick[];
  /** The Now line, or null when now lies outside the window. */
  now: number | null;
};

export const warningLane = (
  slices: readonly WarningLaneSlice[],
  options: WarningTimeOptions,
): WarningLane => {
  const {now, timezone} = options;
  const start = Math.min(
    now - LANE_LEAD_SECONDS,
    ...slices.map((s) => s.start),
  );
  const closedEnds = slices
    .filter((s) => s.end !== null)
    .map((s) => (s.end as number) + LANE_TAIL_SECONDS);
  const openEnd = slices.some((s) => s.end === null)
    ? Math.max(now, ...slices.map((s) => s.start)) + OPEN_END_SECONDS
    : -Infinity;
  // An open end reaches a day past the last start, and never cuts a later
  // closed end off the window.
  const end = Math.max(openEnd, ...closedEnds);
  const span = end - start;
  const at = (t: number) => (t - start) / span;

  const segments: WarningLaneSegment[] = [];
  // Slices of different levels can overlap (a severe hour inside a moderate
  // day is its own slice): drawn mildest first, the worse one on top.
  const painted = [...slices].sort(
    (a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone],
  );
  for (const slice of painted) {
    const a = Math.max(slice.start, start);
    const b = Math.min(slice.end ?? end, end);
    if (b <= a) {
      continue;
    }
    const width = (b - a) / span;
    segments.push({
      id: slice.id,
      tone: slice.tone,
      severity: slice.severity,
      left: at(a),
      width,
      showWord:
        slice.severity !== null &&
        slices.length <= WORD_MAX_SEGMENTS &&
        width > WORD_MIN_WIDTH,
    });
  }

  const ticks: WarningLaneTick[] = [];
  // Calendar days, not 86,400 s steps: a DST change makes a day 23 or 25 h.
  const midnight = Dates.timestampToMoment(start, timezone).startOf('day');
  for (
    midnight.add(1, 'day');
    midnight.unix() <= end && ticks.length < MAX_TICKS;
    midnight.add(1, 'day')
  ) {
    const t = midnight.unix();
    if (at(t) < TICK_MAX_LEFT) {
      ticks.push({at: t, left: at(t), label: laneDayLabel(t, options)});
    }
  }

  return {
    start,
    end,
    segments,
    ticks,
    now: now >= start && now <= end ? at(now) : null,
  };
};

/**
 * The list group (lane, period rows, window) a warning row belongs to, as
 * the list reads it now: the detail screen draws the same lane. Found through
 * the rows merged into each spell, so a row folded into an earlier one's
 * slice is still found. Null once none of its hazard's rows is current.
 */
export const listGroupFor = (
  list: WarningListViewModel,
  warning: WeatherWarning,
  rows: readonly WeatherWarning[],
  now: number,
): WarningListGroup | null => {
  const all = [warning, ...rows.filter((r) => r.id !== warning.id)];
  // Its spell over every row, expired ones included (as `detailViewModel`
  // reads it): AEMET's today row has expired at 00:30 while the episode it
  // opened goes on in tomorrow's row.
  const spell = groupByHazard(all, Number.NEGATIVE_INFINITY)
    .flatMap((g) => g.slices)
    .find((s) => s.warnings.some((w) => w.id === warning.id));
  const ids = new Set((spell?.warnings ?? [warning]).map((w) => w.id));
  const group = groupByHazard(all, now).find((g) =>
    g.slices.some((s) => s.warnings.some((w) => ids.has(w.id))),
  );
  if (!group) {
    return null;
  }
  return (
    list.sections
      .flatMap((section) => section.groups)
      .find((g) => g.key === group.key) ?? null
  );
};

/** The window a group's lane covers: first start to last end (null: open). */
export const warningGroupWindow = (
  group: Pick<WarningListGroup, 'slices'>,
): {start: number; end: number | null} => ({
  start: Math.min(...group.slices.map((s) => s.start)),
  end: group.slices.some((s) => s.end === null)
    ? null
    : Math.max(...group.slices.map((s) => s.end as number)),
});

/**
 * What the warnings list / page shows, before its own empty state:
 *
 * - `error`: the last fetch failed and nothing fresh is stored. An outage
 *   must never read as "no warnings".
 * - `pending`: no response yet and nothing failed: show nothing (no
 *   loading placeholder, target 9a).
 * - `ready`: the stored response, current or a little stale (a refresh is
 *   under way, or a failed refresh over data still under the hour).
 */
export const warningListState = ({
  hasError,
  isStale,
  hasResponse,
}: {
  hasError: boolean;
  isStale: boolean;
  hasResponse: boolean;
}): 'error' | 'pending' | 'ready' => {
  if (hasError && isStale) {
    return 'error';
  }
  return hasResponse ? 'ready' : 'pending';
};
